<template>
  <div
      class="z-10 static md:relative w-full"
      v-on-clickaway="clearQuery"
      data-search-container
  >
    <div
        class="relative"
        @keydown.up.prevent="moveSelectionUp()"
        @keydown.down.prevent="moveSelectionDown()"
        @keydown.enter.prevent="confirmSelection()"
        @keydown.esc.prevent="close()"
    >
      <TextInput
          class="w-full athlete-search"
          v-model="query"
          :icon="
          showCloseIcon
            ? 'sr-2 sr-v2-x text-gray-500'
            : showSearchIcon
            ? 'sr-2 sr-v2-magnifying-glass text-gray-500'
            : showLoadIcon
            ? 'sr-2 sr-v2-spinner text-blue-300 fa-spin'
            : null
        "
          @iconClick="showCloseIcon && close()"
          data-qa="search_box"
          data-test-id="search-box"
          placeholder="Search..."
          :noIconBorder="true"
          ref="athleteSearchInput"
          @focus="togglePlaceholder(true)"
          @keyup.native="togglePlaceholder(true)"
          @blur="togglePlaceholder(false)"
      />
    </div>
    <div
        class="absolute inset-0 bg-white md:rounded shadow-none md:shadow-md md:block md:inset-auto athlete-search-results-container overflow-y-auto mx-0 mt-14 min-w-0 md:mt-2"
        v-if="placeholder"
    >
      <SearchPlaceholder/>
    </div>

    <div
        class="fixed bg-white shadow-none md:rounded md:shadow-md md:absolute md:right-auto md:bottom-auto athlete-search-results-container-wrapper"
        data-results-container
        v-if="showResults"
    >
      <div
          class="absolute inset-0 bg-white rounded shadow-none md:shadow-md md:block md:inset-auto athlete-search-results-container overflow-y-auto"
      >
        <Results
            :results="results"
            :query="query"
            @clickedResult="onClickedResult"
            :selectedIndex="selectedIndex"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Results from "./Results";
import SearchPlaceholder from "./SearchPlaceholder";
import {TextInput} from '@sportsrecruits/playbook';
import debounce from "lodash/debounce";
import api from "@/services/api";
import denormalize from "@/utils/denormalizeJsonApi";
import memoize from "memoizee";
import {mixin as clickaway} from "vue-clickaway";
import uuid from "uuid";
import media from "@/mixins/media";

const searchId = uuid();
const search = memoize((query, searchId, queryId) =>  api.get("api/v1/search/primary", { search: query, searchId: searchId, queryId: queryId }));

export default {
  mixins: [clickaway, media],
  props: {
    isOpen: Boolean,
  },
  components: {
    Results,
    TextInput,
    SearchPlaceholder,
  },
  data() {
    return {
      query: "",
      results: null,
      queryThatCurrentResultsAreFor: null,
      retrieval: null,
      queryId: null,
      selectedIndex: undefined,
      placeholder: false,
    };
  },

  computed: {
    isMobile() {
      return ["xs", "sm", "mobile"].includes(this.$mq);
    },
    hasQuery() {
      return !!this.query;
    },

    showResults() {
      return Boolean(this.hasQuery && this.results);
    },

    showSearchIcon() {
      return this.isDesktop && !this.showCloseIcon && !this.showLoadIcon;
    },

    showCloseIcon() {
      return (this.isMobile || this.hasQuery) && !this.showLoadIcon;
    },

    showLoadIcon() {
      return this.query && this.queryThatCurrentResultsAreFor !== this.query;
    },
  },

  methods: {
    async retrieve() {
      const query = this.query;

      const retrieval = (this.retrieval = search(query, searchId, this.queryId));

      const response = await retrieval;

      if (this.retrieval === retrieval) {
        this.retrieval = null;
        this.queryThatCurrentResultsAreFor = query;
        this.results = denormalize(response);
      }
    },

    clearQuery() {
      this.query = "";
    },

    close() {
      this.clearQuery();
      this.$emit("close");
    },

    updateSearch: debounce(function () {
      if (this.query) {
        this.retrieve();
      }
    }, 2000),

    confirmSelection() {
      if (typeof this.selectedIndex !== "undefined") {
        const resultItem = this.results[this.selectedIndex];
        this.onClickedResult(resultItem);
      }
    },

    onClickedResult(resultItem) {
      let uriMap = {
        'site_user': '/athlete/',
        'club': '/organization/'
      };

      api.post("/api/v1/search/primary/viewed", {
        searchId: searchId,
        queryId: this.queryId,
        clickedId: resultItem.id,
        clickedType: resultItem.resultType,
      }).then(function () {
        localStorage.setItem("view_source", "search");
        location.href = uriMap[resultItem.resultType] + resultItem.url;
      });
    },

    moveSelectionDown(e) {
      if (this.results && this.results.length) {
        if (typeof this.selectedIndex === "undefined") {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex = (this.selectedIndex + 1) % this.results.length;
        }
      }
    },

    moveSelectionUp(e) {
      if (this.results && this.results.length) {
        if (typeof this.selectedIndex === "undefined") {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex--;
          if (this.selectedIndex < 0) {
            this.selectedIndex += this.results.length;
          }
        }
      }
    },

    togglePlaceholder(state) {
      this.placeholder = state && this.query.length == 0;
    },

  },

  watch: {
    query(query) {
      this.queryId = uuid();
      if (query) {
        this.updateSearch();
      } else {
        this.results = null;
        this.retrieval = null;
        this.queryThatCurrentResultsAreFor = null;
      }
    },

    results() {
      this.selectedIndex = undefined;
    },
    showResults(hasResults) {
      if (hasResults) {
        document.body.classList.add("overflow-hidden", "max-h-screen");
      } else {
        document.body.classList.remove("overflow-hidden", "max-h-screen");
      }
    },
    isOpen(bool) {
      if (bool) {
        this.$nextTick(() => {
          this.$refs.athleteSearchInput.$el.children[0].focus();
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.athlete-search-results-container-wrapper {
  top: 58px;
  bottom: 0px;
  width: 100vw !important;
  left: 0px;
}

.athlete-search-results-container {
  min-width: 100%;
}

@media (min-width: 0) and (max-width: 767px) {
  .athlete-search-results-container {
    height: calc(100vh - 56px);
  }
}

@media screen and (min-width: 768px) {
  .athlete-search-results-container-wrapper {
    bottom: 0px;
    top: 48px;
  }
  .athlete-search-results-container {
    min-width: 440px !important;
    max-width: 440px;
    height: auto;
  }
}
</style>